import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { CATS } from "@/lib/constants";
import { FieldLabel } from "@/components/Chip";
import { StartPicker } from "@/components/StartPicker";

const inputCls = "w-full h-10 rounded-lg border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F7D55]/40";
const EMPTY = { title: "", offer: "", description: "", category: "cafe", location: null, link: "", time_slots: [{ start: "11:00", end: "14:00" }] };

export const AdFormDialog = ({ open, onOpenChange, ad, onSaved }) => {
  const [f, setF] = useState(ad ? { ...ad, link: ad.link || "" } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const setSlot = (i, k, v) => set("time_slots")(f.time_slots.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  const submit = async () => {
    setBusy(true);
    try {
      const body = { title: f.title, offer: f.offer, description: f.description, category: f.category, location: f.location || {}, link: f.link, time_slots: f.time_slots };
      const { data } = ad ? await api.put(`/business/ads/${ad.id}`, body) : await api.post("/business/ads", body);
      toast.success("Submitted for review. We'll check it before it goes live.");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[1250] sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ad-form-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl">{ad ? "Edit ad" : "New ad"}</DialogTitle>
          <DialogDescription>Every ad is reviewed by Outbound before it goes live{ad ? ", including edits" : ""}. Members always see it labeled as sponsored.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><FieldLabel>Title</FieldLabel><input data-testid="ad-title-input" value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder="e.g. Afternoon pick-me-up" className={inputCls} /></div>
          <div><FieldLabel>Deal / offer</FieldLabel><input data-testid="ad-offer-input" value={f.offer} onChange={(e) => set("offer")(e.target.value)} placeholder="e.g. 2-for-1 iced coffee" className={inputCls} /></div>
          <div><FieldLabel>Description</FieldLabel><textarea data-testid="ad-description-input" rows={2} value={f.description} onChange={(e) => set("description")(e.target.value)} className="w-full rounded-lg border border-stone-300 p-3 text-sm" /></div>
          <div><FieldLabel>Category</FieldLabel>
            <select data-testid="ad-category-select" value={f.category} onChange={(e) => set("category")(e.target.value)} className={inputCls}>
              {Object.entries(CATS).filter(([k]) => k !== "anchor").map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select></div>
          <div><FieldLabel>Business address (shown on the map)</FieldLabel><StartPicker value={f.location} onChange={set("location")} testPrefix="ad-location" /></div>
          <div><FieldLabel>Deal time slots (the deal is only shown during these times)</FieldLabel>
            <div className="space-y-2">
              {f.time_slots.map((s, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`ad-slot-${i}`}>
                  <input type="time" data-testid={`ad-slot-start-${i}`} value={s.start} onChange={(e) => setSlot(i, "start", e.target.value)} className={inputCls} />
                  <span className="text-stone-400">to</span>
                  <input type="time" data-testid={`ad-slot-end-${i}`} value={s.end} onChange={(e) => setSlot(i, "end", e.target.value)} className={inputCls} />
                  <button type="button" data-testid={`ad-slot-remove-${i}`} disabled={f.time_slots.length === 1} onClick={() => set("time_slots")(f.time_slots.filter((_, j) => j !== i))} className="p-2 rounded-lg border hover:bg-stone-100 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button type="button" data-testid="ad-slot-add" onClick={() => set("time_slots")([...f.time_slots, { start: "17:00", end: "19:00" }])} className="text-sm font-semibold text-[#23603F] flex items-center gap-1"><Plus className="w-4 h-4" /> Add time slot</button>
            </div></div>
          <div><FieldLabel>Link (optional)</FieldLabel><input data-testid="ad-link-input" value={f.link} onChange={(e) => set("link")(e.target.value)} placeholder="https://" className={inputCls} /></div>
          <button data-testid="ad-submit-btn" onClick={submit} disabled={busy} className="w-full bg-[#2F7D55] hover:bg-[#23603F] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50">
            {busy ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
